import "./styles.css";
import { el, clear } from "./dom";
import { consumeHashKey, loadKey, saveKey, clearKey } from "./auth";
import {
  listAlerts,
  createAlert,
  rearmAlert,
  deleteAlert,
  getEmail,
  setEmail,
  friendlyError,
  ApiError,
  type Alert,
} from "./api";
import { validateCreate, validateEmail } from "./validation";

const store = window.localStorage;
const app = document.getElementById("app")!;

function setMessage(node: HTMLElement, text: string, kind: "error" | "ok" = "error") {
  node.textContent = text;
  node.className = `message ${text ? kind : ""}`;
}

// ---- Login screen --------------------------------------------------------

function renderLogin() {
  stopPricePolling();
  clear(app);

  const error = el("p", { class: "message" });
  const input = el("input", {
    type: "text",
    placeholder: "Paste your key",
    autocomplete: "off",
    spellcheck: "false",
  }) as HTMLInputElement;

  const form = el("form", {
    onsubmit: (e: Event) => {
      e.preventDefault();
      const key = input.value.trim();
      if (!key) {
        setMessage(error, "Paste the key from your link.");
        return;
      }
      saveKeyAndGo(key);
    },
  }, [
    el("h1", {}, ["BTC Price Alerts"]),
    el("p", { class: "subtle" }, ["Log in with the key from your magic link."]),
    input,
    el("button", { type: "submit" }, ["Log in"]),
    error,
  ]);

  const contact = el("p", { class: "subtle contact" });
  fetch("/config")
    .then((r) => r.json())
    .then((c: { telegramUrl?: string; email?: string }) => {
      if (!c.telegramUrl && !c.email) return;
      contact.append(document.createTextNode("Need a key? "));
      if (c.telegramUrl) {
        const tg = el("a", {
          href: c.telegramUrl,
          target: "_blank",
          rel: "noopener",
          class: "tg-link",
        });
        tg.append(telegramIcon(), document.createTextNode("Message me on Telegram"));
        contact.append(tg);
      }
      if (c.email) {
        if (c.telegramUrl) contact.append(document.createTextNode(" · "));
        contact.append(el("a", { href: `mailto:${c.email}` }, ["Email me"]));
      }
    })
    .catch(() => {});

  app.append(el("div", { class: "card" }, [form, contact]));
}

function saveKeyAndGo(key: string) {
  saveKey(store, key);
  renderDashboard(key);
}

// Official Telegram mark (blue disc + white paper plane), inline so there's no
// asset/network dependency. Static trusted markup — safe to set via innerHTML.
function telegramIcon(): HTMLSpanElement {
  const span = document.createElement("span");
  span.className = "tg-icon";
  span.setAttribute("aria-hidden", "true");
  span.innerHTML =
    '<svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">' +
    '<circle cx="12" cy="12" r="12" fill="#2AABEE"/>' +
    '<path fill="#fff" d="M5.49 11.78l11.04-4.26c.51-.18.96.12.79.9l-1.88 8.86c-.13.6-.49.74-1 .46l-2.76-2.04-1.33 1.28c-.15.15-.27.27-.55.27l.2-2.82 5.13-4.63c.22-.2-.05-.31-.34-.11l-6.34 3.99-2.73-.85c-.59-.19-.6-.59.13-.88z"/>' +
    "</svg>";
  return span;
}

// ---- Live BTC price ticker ----------------------------------------------

const PRICE_POLL_MS = 30_000;
let priceTimer: ReturnType<typeof setInterval> | undefined;

async function refreshPrice(el: HTMLElement) {
  try {
    const r = await fetch("/price");
    if (!r.ok) throw new Error("price unavailable");
    const { usd } = (await r.json()) as { usd: number };
    el.textContent = `BTC $${usd.toLocaleString("en-US")}`;
    el.classList.remove("stale");
  } catch {
    el.textContent = "BTC price unavailable";
    el.classList.add("stale");
  }
}

function startPricePolling(el: HTMLElement) {
  stopPricePolling();
  refreshPrice(el);
  priceTimer = setInterval(() => refreshPrice(el), PRICE_POLL_MS);
}

function stopPricePolling() {
  if (priceTimer) {
    clearInterval(priceTimer);
    priceTimer = undefined;
  }
}

// ---- Dashboard screen ----------------------------------------------------

function renderDashboard(key: string) {
  stopPricePolling();
  clear(app);

  const price = el("span", { class: "price", title: "Live BTC price via CoinGecko" }, ["…"]);
  startPricePolling(price);

  const header = el("div", { class: "header" }, [
    el("div", { class: "title-row" }, [el("h1", {}, ["Your alerts"]), price]),
    el("button", {
      class: "ghost",
      onclick: () => {
        stopPricePolling();
        clearKey(store);
        renderLogin();
      },
    }, ["Log out"]),
  ]);

  const content = el("div", { class: "content" }, ["Loading…"]);
  app.append(el("div", { class: "card" }, [header, content]));

  // Decide the screen from the tenant's email profile: gate until it's set,
  // then show the alerts UI. Re-run whenever the email goes from unset->set
  // (or a create hits 409 and we discover it's actually unset).
  async function load() {
    clear(content);
    content.append(document.createTextNode("Loading…"));
    try {
      const email = await getEmail(key);
      clear(content);
      if (email == null) {
        renderEmailGate(content, key, load);
      } else {
        renderAlerts(content, key, email, load);
      }
    } catch (err) {
      clear(content);
      const message = el("p", { class: "message" });
      setMessage(message, friendlyError(err));
      content.append(message);
      if (err instanceof ApiError && err.status === 403) {
        // stale/invalid key — bounce to login so the user can paste a fresh one
        stopPricePolling();
        clearKey(store);
        renderLogin();
      }
    }
  }

  load();
}

// One-time setup: shown until the tenant has a notification email. Saving it
// calls onSaved() to re-evaluate the screen and reveal the alerts UI.
function renderEmailGate(content: HTMLElement, key: string, onSaved: () => void) {
  const message = el("p", { class: "message" });
  const input = el("input", { type: "email", placeholder: "you@example.com" }) as HTMLInputElement;
  const submit = el("button", { type: "submit" }, ["Save email"]) as HTMLButtonElement;

  const form = el("form", {
    class: "email-setup",
    onsubmit: async (e: Event) => {
      e.preventDefault();
      setMessage(message, "");
      const result = validateEmail(input.value);
      if (!result.ok) {
        setMessage(message, result.error);
        return;
      }
      submit.disabled = true;
      try {
        await setEmail(key, result.email);
        onSaved();
      } catch (err) {
        setMessage(message, friendlyError(err));
      } finally {
        submit.disabled = false;
      }
    },
  }, [
    el("h2", {}, ["Set your notification email"]),
    el("p", { class: "subtle" }, [
      "We'll send price alerts here. Set it once — it applies to every alert you create.",
    ]),
    input,
    submit,
    message,
  ]);

  content.append(form);
  input.focus();
}

// The alerts UI proper: an editable "Notifying:" control, the create form, and
// the alerts table. onEmailCleared() re-evaluates the screen (used if the
// server reports no email on create, i.e. a 409).
function renderAlerts(
  content: HTMLElement,
  key: string,
  email: string,
  onEmailCleared: () => void,
) {
  const message = el("p", { class: "message" });
  const tableWrap = el("div", { class: "table-wrap" }, ["Loading…"]);

  const notify = buildNotifyControl(key, email, message);
  const form = buildCreateForm(key, message, () => refresh(), onEmailCleared);
  content.append(notify, form, message, tableWrap);

  async function refresh() {
    try {
      const alerts = await listAlerts(key);
      renderTable(tableWrap, alerts, key, message, refresh);
    } catch (err) {
      clear(tableWrap);
      setMessage(message, friendlyError(err));
      if (err instanceof ApiError && err.status === 403) {
        stopPricePolling();
        clearKey(store);
        renderLogin();
      }
    }
  }

  refresh();
}

// "Notifying: x@y.com [change]" — clicking [change] swaps to an inline editor
// backed by PUT /api/email. Editing here updates delivery for all alerts.
function buildNotifyControl(key: string, initialEmail: string, message: HTMLElement): HTMLElement {
  const wrap = el("div", { class: "notify" });
  let current = initialEmail;

  function showDisplay() {
    clear(wrap);
    wrap.append(
      el("span", { class: "subtle" }, ["Notifying: "]),
      el("strong", {}, [current]),
      el("button", { type: "button", class: "ghost link", onclick: showEdit }, ["change"]),
    );
  }

  function showEdit() {
    clear(wrap);
    const input = el("input", { type: "email", value: current }) as HTMLInputElement;
    const save = el("button", { type: "submit" }, ["Save"]) as HTMLButtonElement;
    const cancel = el("button", { type: "button", class: "ghost", onclick: showDisplay }, ["Cancel"]);
    const fieldError = el("p", { class: "message" });

    const form = el("form", {
      class: "notify-edit",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        setMessage(fieldError, "");
        const result = validateEmail(input.value);
        if (!result.ok) {
          setMessage(fieldError, result.error);
          return;
        }
        save.disabled = true;
        try {
          current = await setEmail(key, result.email);
          showDisplay();
          setMessage(message, "Notification email updated.", "ok");
        } catch (err) {
          // Surface the server's 400 "invalid email address" under the field.
          setMessage(fieldError, friendlyError(err));
        } finally {
          save.disabled = false;
        }
      },
    }, [
      el("div", { class: "field-row" }, [input, save, cancel]),
      fieldError,
    ]);

    wrap.append(form);
    input.focus();
  }

  showDisplay();
  return wrap;
}

function buildCreateForm(
  key: string,
  message: HTMLElement,
  onCreated: () => void,
  onNeedEmail: () => void,
) {
  const target = el("input", { type: "number", step: "any", placeholder: "Target price (USD)" }) as HTMLInputElement;
  const pct = el("input", { type: "number", step: "any", placeholder: "or % move" }) as HTMLInputElement;
  const submit = el("button", { type: "submit" }, ["Create alert"]) as HTMLButtonElement;

  return el("form", {
    class: "create",
    onsubmit: async (e: Event) => {
      e.preventDefault();
      setMessage(message, "");
      const result = validateCreate({ targetPrice: target.value, pct: pct.value });
      if (!result.ok) {
        setMessage(message, result.error);
        return;
      }
      submit.disabled = true;
      try {
        await createAlert(key, result.body);
        target.value = "";
        pct.value = "";
        setMessage(message, "Alert created.", "ok");
        onCreated();
      } catch (err) {
        setMessage(message, friendlyError(err));
        if (err instanceof ApiError && err.status === 409) {
          // Tenant has no profile email — route them back to the setup gate.
          onNeedEmail();
        }
      } finally {
        submit.disabled = false;
      }
    },
  }, [target, pct, submit]);
}

function renderTable(
  wrap: HTMLElement,
  alerts: Alert[],
  key: string,
  message: HTMLElement,
  refresh: () => void,
) {
  clear(wrap);
  if (alerts.length === 0) {
    wrap.append(el("p", { class: "subtle" }, ["No alerts yet — create one above."]));
    return;
  }

  const rows = alerts.map((a) => {
    const trigger = a.pct != null ? `${a.pct}%` : `$${a.targetPrice.toLocaleString()}`;
    const actions: Node[] = [];
    if (a.status === "FIRED") {
      actions.push(el("button", {
        class: "ghost",
        onclick: () => act(() => rearmAlert(key, a.id)),
      }, ["Rearm"]));
    }
    actions.push(el("button", {
      class: "ghost danger",
      onclick: () => act(() => deleteAlert(key, a.id)),
    }, ["Delete"]));

    return el("tr", {}, [
      el("td", {}, [el("span", { class: `pill ${a.status.toLowerCase()}` }, [a.status])]),
      el("td", {}, [a.direction]),
      el("td", {}, [trigger]),
      el("td", { class: "actions" }, actions),
    ]);
  });

  async function act(fn: () => Promise<void>) {
    setMessage(message, "");
    try {
      await fn();
      refresh();
    } catch (err) {
      setMessage(message, friendlyError(err));
    }
  }

  wrap.append(
    el("table", {}, [
      el("thead", {}, [
        el("tr", {}, [
          el("th", {}, ["Status"]),
          el("th", {}, ["Direction"]),
          el("th", {}, ["Trigger"]),
          el("th", {}, [""]),
        ]),
      ]),
      el("tbody", {}, rows),
    ]),
  );
}

// ---- Boot ----------------------------------------------------------------

consumeHashKey(window.location, window.history, store);
const existing = loadKey(store);
if (existing) {
  renderDashboard(existing);
} else {
  renderLogin();
}
